<template>
  <!-- No page header: the toolbar's back button and position readout already say
       where you are, and the header cost a row of the reachout's height. -->
  <PageChrome fill tight :show-header="false">
    <div class="detailPage">
      <!-- Same bar as the list, attached to the top of the reachout. Here the
           pager steps reachout-to-reachout rather than page-to-page, so the
           control keeps its position as you drill in and out.

           No bulk-action buttons in detail mode: ReachoutModal's footer already
           owns Delete / Revise / Edit / Approve for the open reachout, with
           per-reachout enablement (approval blocks, edit state) this bar can't
           see. Two Approve buttons that disagree would be worse than one. -->
      <ActionsToolbar
        mode="detail"
        :range-label="positionLabel"
        :can-prev="canPrev"
        :can-next="canNext"
        @back="query.backToList()"
        @prev="goPrev"
        @next="goNext"
      />

      <div v-if="query.error.value" class="pageError">{{ query.error.value }}</div>
      <div v-if="loadError" class="pageError">{{ loadError }}</div>

      <div v-if="reachout" class="detailPaneContent">
        <ReachoutModal
          :reachout="reachout"
          mode="embedded"
          @updated="onUpdated"
          @deleted="onDeleted"
        />
      </div>
      <div v-else-if="loading" class="detailEmpty">Loading…</div>
      <div v-else class="detailEmpty">Reachout not found.</div>
    </div>

    <template #footer>
      <ShortcutBar :hotkeys="hotkeys" />
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import ReachoutModal from '../../components/ReachoutModal.vue'
import ActionsToolbar from '../components/actions/ActionsToolbar.vue'
import { useHotkeys, type Hotkey } from '../composables/useHotkeys'
import ShortcutBar from '../components/ShortcutBar.vue'
import { ACTIONS_PAGE_SIZE, useActionsQuery } from '../composables/useActionsQuery'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_REACHOUT } from '../../drawer/drawerTypes'

/**
 * Full-area reachout detail — the Gmail "open message" state.
 *
 * The URL carries the list's filters alongside the id
 * (`/actions/<id>?type=…&p=2`), so this page can rebuild the same ordered page
 * the list showed and answer what j/k should move to. That's also why "back to
 * list" needs no history: it's the current query minus the id segment.
 */

const route = useRoute()
const { setContext: setDrawerContext, pinDrawer } = useContextDrawer()
pinDrawer()
const reachoutId = computed(() => String(route.params.reachoutId || ''))

const query = useActionsQuery()

const reachout = ref<any>(null)
const loading = ref(false)
const loadError = ref('')

async function loadReachout({ background = false } = {}) {
  const id = reachoutId.value
  if (!id) return
  if (!background) loading.value = true
  loadError.value = ''
  try {
    const fetched = await apiFetch(`/reachouts/${id}`)
    if (reachoutId.value === id) reachout.value = fetched
  } catch (e: any) {
    if (!background) {
      reachout.value = null
      loadError.value = e?.message || 'Failed to load reachout.'
    }
  } finally {
    if (!background) loading.value = false
  }
}

watch(reachoutId, (id) => {
  const cached = id ? query.items.value.find((r: any) => String(r.id) === id) : null
  if (cached) {
    reachout.value = cached
    void loadReachout({ background: true })
    return
  }
  void loadReachout()
}, { immediate: true })

// Keep the shared list cursor on whatever is open here, so returning to the list
// lands on this row rather than the one you originally opened.
watch(reachoutId, (id) => { if (id) query.cursorId.value = id }, { immediate: true })

/**
 * Context drawer (the `\` panel).
 *
 * Driven off the fetched reachout rather than the route id, because the drawer's
 * cards need accountId / contactId / agentRunId, which only the row carries.
 * That also means the context lands once per j/k step instead of twice — the
 * drawer debounces, but publishing a reachoutId-only context first would make
 * the cards fetch against a half-populated context.
 */
watch(reachout, (current) => {
  const accountId = String(current?.account_id || '').trim()
  const id = String(current?.id || '').trim()
  if (!id || !accountId) {
    setDrawerContext(null)
    return
  }
  setDrawerContext({
    type: DRAWER_CONTEXT_REACHOUT,
    reachoutId: id,
    accountId,
    contactId: String(current?.contact_id || '').trim(),
    agentRunId: String(current?.created_by_agent_run_id || '').trim(),
  })
}, { immediate: true })

// Leaving the page shouldn't strand the drawer on a reachout that's no longer open.
onBeforeUnmount(() => setDrawerContext(null))

const currentIndex = computed(() =>
  query.items.value.findIndex((item: any) => String(item.id) === reachoutId.value),
)

const positionLabel = computed(() => {
  const index = currentIndex.value
  if (index === -1 || !query.total.value) return ''
  const absolute = (query.filters.value.page - 1) * ACTIONS_PAGE_SIZE + index + 1
  return `${absolute} of ${query.total.value}`
})

// A row on a page boundary can still advance — the composable pages across.
const canPrev = computed(() => currentIndex.value > 0 || query.filters.value.page > 1)
const canNext = computed(() =>
  (currentIndex.value !== -1 && currentIndex.value < query.items.value.length - 1)
  || query.filters.value.page < query.pageCount.value,
)

async function goNext() {
  const id = await query.idAfter(reachoutId.value)
  if (id) query.openDetail(id)
}

async function goPrev() {
  const id = await query.idBefore(reachoutId.value)
  if (id) query.openDetail(id)
}

/**
 * Whether the row is still in the filtered set is answered by the server, not by
 * re-deriving the stage buckets here — that duplication is what let the old
 * client-side status lists drift out of sync. Refetch, then look.
 */
async function onUpdated() {
  const id = reachoutId.value
  // The neighbour has to be read from the page as it stands now: the refetch is
  // what drops this row, and idAfter steps from this row's position in `items`.
  const next = await query.idAfter(id)
  await query.load()
  void query.loadCounts()
  if (query.items.value.some((r: any) => String(r.id) === id)) {
    // Still in the bucket (an edit, not an approve) — stay put and show the edit.
    void loadReachout({ background: true })
    return
  }
  if (next) query.openDetail(next)
  else query.backToList()
}

async function onDeleted() {
  const next = await query.idAfter(reachoutId.value)
  if (next) query.openDetail(next)
  else query.backToList()
  await query.refresh()
}


const hotkeys: Hotkey[] = [
  { key: 'j', handler: () => { void goNext() }, description: 'next', primary: true, group: 'Navigate' },
  { key: 'k', handler: () => { void goPrev() }, description: 'previous', primary: true, group: 'Navigate' },
  { key: 'u', handler: () => query.backToList(), description: 'back to list', primary: true, group: 'Navigate' },
  {
    key: 'delete',
    handler: (e) => { e.preventDefault(); triggerSkip() },
    description: 'delete',
    primary: true,
    group: 'This reachout',
  },
  {
    key: 'mod+r',
    handler: (e) => { e.preventDefault(); triggerRevise() },
    description: 'revise',
    primary: true,
    group: 'This reachout',
  },  
  { key: 'e', handler: triggerEdit, description: 'edit', primary: true, group: 'This reachout' },
  {
    key: 'mod+s',
    handler: (e) => { e.preventDefault(); triggerSave() },
    description: 'save changes',
    group: 'Editing',
  },
  {
    key: 'mod+shift+d',
    handler: (e) => { e.preventDefault(); triggerDiscard() },
    description: 'discard changes',
    group: 'Editing',
  },
  {
    key: 'mod+enter',
    handler: (e) => { e.preventDefault(); triggerApprove() },
    description: 'approve & send',
    primary: true,
    group: 'This reachout',
  },
]

useHotkeys(hotkeys)

// Carried over from the old page: reach into the embedded modal's footer by
// button text. Fragile, but replacing it means changing ReachoutModal's API.
// Clicking the real button is what makes these inherit its enablement — a
// disabled button ignores the click, so a blocked approval stays blocked.
function clickFooterButton(label: string) {
  const buttons = document.querySelectorAll('.detailPaneContent .modalFooterActions button')
  for (const btn of Array.from(buttons)) {
    if ((btn.textContent || '').trim().toLowerCase().startsWith(label)) {
      (btn as HTMLButtonElement).click()
      return
    }
  }
}

function triggerEdit() { clickFooterButton('edit') }
function triggerSave() { clickFooterButton('save') }
function triggerDiscard() { clickFooterButton('discard') }
function triggerRevise() { clickFooterButton('revise') }
function triggerApprove() { clickFooterButton('approve') }

function triggerSkip() {
  const btn = document.querySelector('.detailPaneContent .modalFooterDanger button.danger')
  if (btn) (btn as HTMLButtonElement).click()
}
</script>

<style scoped>
.detailPage {
  --border: var(--color-border-subtle);
  --border2: var(--color-border-subtle);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --badge-bg: var(--color-bg-subtle);
  --hover-bg: var(--color-bg-subtle);
  --link: var(--color-accent);
  --field-bg: var(--color-bg-subtle);
  --focus-border: var(--color-accent);

  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}
/* Continues the toolbar's panel: square top (the bar supplies that divider),
   rounded bottom so the joined bar + reachout closes off like the list's table. */
.detailPaneContent {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-border-subtle);
  border-top: none;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  background: var(--color-bg-app);
}
/* Final link: the Modal shell is a child component, so its height has to be set
   from here. Without this the shell sizes to content and the whole chain above
   is inert. */
.detailPaneContent :deep(.shell.mode-embedded) {
  --surface: var(--color-bg-app);
  --panel: var(--color-bg-app);
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
/* Nested real modals sit inside the embedded shell and would inherit its beige.
   mode="modal" and the dismiss overlay are both true dialogs — white. */
.detailPaneContent :deep(.shell:not(.mode-embedded)),
.detailPaneContent :deep(.dismissOverlay) {
  --surface: var(--color-bg-surface);
  --panel: var(--color-bg-surface);
}

.detailEmpty { padding: var(--space-6); color: var(--color-text-tertiary); font-size: var(--fs-sm); }
.pageError { padding: var(--space-3) var(--space-4); color: var(--color-danger); font-size: var(--fs-sm); }

.backLink {
  display: inline-flex; align-items: center; gap: var(--space-1);
  background: none; border: none; cursor: pointer; padding: 0;
  color: var(--color-text-secondary); font-size: var(--fs-sm);
}
.backLink:hover { color: var(--color-text-primary); }
.backArrow { transform: rotate(180deg); }

.navGroup { display: inline-flex; align-items: center; gap: var(--space-2); margin-left: var(--space-4); }
.navPosition { font-size: var(--fs-xs); color: var(--color-text-tertiary); font-variant-numeric: tabular-nums; }
.navBtn { padding: var(--space-1) var(--space-2); }
</style>
